import { useEffect, useState } from 'react';
import { and, isControl, rankWith, schemaMatches, ControlProps } from '@jsonforms/core';
import { withJsonFormsControlProps, useJsonForms } from '@jsonforms/react';
import { Box, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';

interface PathPreviewConfig {
  /** Where to ask for the resolved path. */
  endpoint?: string;
  /** The form property holding the connection the path is relative to. */
  connectionProperty?: string;
}

/**
 * A path fragment, with the full location it resolves to shown underneath.
 *
 * The resolution is the server's: the form has neither the tenant's container nor the connection's
 * own container and base path, and a second implementation here would be a second rule — the one
 * the user reads would eventually stop being the one that runs.
 *
 * Same shape as the principal picker's "resolves to N users": the control, then a caption stating
 * what the value actually means once everything around it is taken into account.
 */
const PathPreviewRendererComponent = ({
  data,
  handleChange,
  path,
  label,
  schema,
  errors,
  enabled,
  visible,
  description,
}: ControlProps) => {
  const { t } = useTranslation();
  const config: PathPreviewConfig = (schema as any)?.['x-path-preview'] ?? {};
  // Sibling values come from the form context, the way the principal renderer reads the id and
  // qualifier that travel with its own field.
  const ctx = useJsonForms();
  const formData = (ctx.core?.data ?? {}) as Record<string, unknown>;
  const connectionId = config.connectionProperty
    ? (formData[config.connectionProperty] as string | undefined)
    : undefined;

  const [resolved, setResolved] = useState<string | null>(null);

  useEffect(() => {
    if (!config.endpoint || !connectionId) {
      setResolved(null);
      return;
    }

    let cancelled = false;
    // Debounced like the principal picker's lookup: the preview follows typing without a request
    // per keystroke.
    const timer = setTimeout(() => {
      apiClient
        .get(config.endpoint!, { params: { connectionId, deliveryPath: data ?? '' } })
        .then((res) => {
          if (!cancelled) setResolved(res.data?.fullPath ?? null);
        })
        .catch(() => {
          // A preview that cannot be fetched is not an error the user must act on; the field still
          // works and the server still decides.
          if (!cancelled) setResolved(null);
        });
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [config.endpoint, connectionId, data]);

  if (!visible) return null;

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        variant="standard"
        label={label}
        value={data ?? ''}
        disabled={!enabled}
        error={!!errors}
        helperText={errors || description}
        onChange={(e) => handleChange(path, e.target.value || undefined)}
      />
      {resolved && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.5, wordBreak: 'break-all' }}
        >
          {t('pathPreview.resolvesTo', {
            path: resolved,
            defaultValue: `Files land in ${resolved}`,
          })}
        </Typography>
      )}
      {!resolved && connectionId === undefined && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {t('pathPreview.needsConnection', {
            defaultValue: 'Pick a connection to see where files will land.',
          })}
        </Typography>
      )}
    </Box>
  );
};

export const PathPreviewRenderer = withJsonFormsControlProps(PathPreviewRendererComponent);

/** Tester: matches a control whose schema declares the path-preview format. */
export const pathPreviewRendererTester = rankWith(
  10,
  and(isControl, schemaMatches((schema) => (schema as any).format === 'path-preview')),
);

export const pathPreviewRendererEntry = {
  tester: pathPreviewRendererTester,
  renderer: PathPreviewRenderer,
};
